import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Clock, Play, RefreshCw, Calendar, Bell, 
  CheckCircle, AlertTriangle, Users, Mail, History,
  FileText, Timer, Zap
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import Sidebar from "../components/Sidebar";

const API = (process.env.REACT_APP_BACKEND_URL || "");

const EncuestasSchedulerPage = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [campanasProgramadas, setCampanasProgramadas] = useState([]);
  const [recordatoriosPendientes, setRecordatoriosPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({ campaigns: false, reminders: false });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  useEffect(() => {
    fetchAll();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = async () => {
    await Promise.all([
      fetchStatus(),
      fetchLogs(),
      fetchCampanasProgramadas(),
      fetchRecordatoriosPendientes()
    ]);
    setLoading(false);
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/scheduler/status`, getAuthHeaders());
      setStatus(res.data);
    } catch (err) {
      console.error("Error fetching scheduler status:", err);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/scheduler/logs?limite=20`, getAuthHeaders());
      setLogs(res.data);
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

  const fetchCampanasProgramadas = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/scheduler/campanas-programadas`, getAuthHeaders());
      setCampanasProgramadas(res.data);
    } catch (err) {
      console.error("Error fetching campanas programadas:", err);
    }
  };

  const fetchRecordatoriosPendientes = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/scheduler/recordatorios-pendientes`, getAuthHeaders());
      setRecordatoriosPendientes(res.data);
    } catch (err) {
      console.error("Error fetching recordatorios:", err);
    }
  };

  const runTask = async (task) => {
    const key = task === "scheduled_campaigns" ? "campaigns" : "reminders";
    setRunning({ ...running, [key]: true });
    
    try {
      await axios.post(`${API}/api/encuestas/scheduler/run-now?task=${task}`, {}, getAuthHeaders());
      toast.success(`Tarea "${task === "scheduled_campaigns" ? "Campañas Programadas" : "Recordatorios"}" iniciada`);
      // Refresh after 5 seconds
      setTimeout(fetchAll, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar tarea");
    } finally {
      setRunning({ ...running, [key]: false });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-0 md:ml-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const campaignsJob = status?.jobs?.find(j => j.id === "encuestas_scheduled_campaigns");
  const remindersJob = status?.jobs?.find(j => j.id === "encuestas_reminders");

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 overflow-auto ml-0 md:ml-64">
        <div className="p-6 max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Programador de Encuestas</h1>
                <p className="text-slate-500">Gestione los envíos automáticos de encuestas y recordatorios</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status?.scheduler_activo ? (
                <Badge className="bg-green-100 text-green-800 gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Scheduler Activo
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Scheduler Inactivo
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={fetchAll}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Resumen
              </TabsTrigger>
              <TabsTrigger value="campanas" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Campañas Programadas ({campanasProgramadas.length})
              </TabsTrigger>
              <TabsTrigger value="recordatorios" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Recordatorios ({recordatoriosPendientes.length})
              </TabsTrigger>
              <TabsTrigger value="logs" className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historial
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Campañas Programadas */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <CardTitle>Campañas Programadas</CardTitle>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => runTask("scheduled_campaigns")}
                        disabled={running.campaigns}
                      >
                        {running.campaigns ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Ejecutar Ahora
                      </Button>
                    </div>
                    <CardDescription>
                      Envía automáticamente las encuestas cuando llega la fecha programada
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Frecuencia de verificación</span>
                        <Badge variant="outline">{campaignsJob?.frecuencia || "Cada 5 minutos"}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">Campañas pendientes de envío</span>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">
                          {campanasProgramadas.length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recordatorios */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-amber-600" />
                        <CardTitle>Recordatorios Automáticos</CardTitle>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => runTask("reminders")}
                        disabled={running.reminders}
                      >
                        {running.reminders ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Ejecutar Ahora
                      </Button>
                    </div>
                    <CardDescription>
                      Envía recordatorios a clientes que no han respondido la encuesta
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Frecuencia de verificación</span>
                        <Badge variant="outline">{remindersJob?.frecuencia || "Cada 30 minutos"}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-800">Campañas con recordatorios</span>
                        </div>
                        <span className="text-2xl font-bold text-amber-600">
                          {recordatoriosPendientes.length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Info Card */}
              <Card className="mt-6 border-blue-200 bg-blue-50">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-2">Cómo funciona el programador</p>
                      <ul className="space-y-1 text-blue-600">
                        <li>• <strong>Campañas programadas:</strong> Se verifican cada 5 minutos. Cuando la fecha/hora programada se cumple, se envían las invitaciones a todos los destinatarios.</li>
                        <li>• <strong>Recordatorios:</strong> Se verifican cada 30 minutos. Solo se envían a la hora configurada en cada campaña, según los días configurados (1er y 2do recordatorio).</li>
                        <li>• Los botones "Ejecutar Ahora" permiten forzar la ejecución inmediata de cada tarea sin esperar al intervalo.</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Campañas Programadas Tab */}
            <TabsContent value="campanas">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Campañas Pendientes de Envío
                  </CardTitle>
                  <CardDescription>
                    Estas campañas están programadas para enviarse automáticamente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {campanasProgramadas.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay campañas programadas pendientes</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campaña</TableHead>
                          <TableHead>Fecha Programada</TableHead>
                          <TableHead>Hora</TableHead>
                          <TableHead>Destinatarios</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campanasProgramadas.map((campana) => (
                          <TableRow key={campana.id}>
                            <TableCell className="font-medium">{campana.nombre}</TableCell>
                            <TableCell>{campana.fecha_envio_programado}</TableCell>
                            <TableCell>{campana.hora_envio_programado || "09:00"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {campana.destinatarios_pendientes} pendientes
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-800">
                                <Timer className="w-3 h-3 mr-1" />
                                Programada
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Recordatorios Tab */}
            <TabsContent value="recordatorios">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Recordatorios Pendientes
                  </CardTitle>
                  <CardDescription>
                    Campañas con destinatarios que necesitan recordatorio
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {recordatoriosPendientes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p>No hay recordatorios pendientes</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campaña</TableHead>
                          <TableHead>Pendientes</TableHead>
                          <TableHead>Días 1er Recordatorio</TableHead>
                          <TableHead>Máx. Recordatorios</TableHead>
                          <TableHead>Hora de Envío</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recordatoriosPendientes.map((item) => (
                          <TableRow key={item.campana_id}>
                            <TableCell className="font-medium">{item.campana_nombre}</TableCell>
                            <TableCell>
                              <Badge variant="destructive">
                                {item.destinatarios_pendientes_recordatorio}
                              </Badge>
                            </TableCell>
                            <TableCell>{item.config?.dias_primer_recordatorio || 3} días</TableCell>
                            <TableCell>{item.config?.max_recordatorios || 2}</TableCell>
                            <TableCell>{item.config?.hora_envio || "09:00"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Historial de Ejecuciones
                  </CardTitle>
                  <CardDescription>
                    Últimas ejecuciones del programador
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {logs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay logs de ejecución todavía</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Inicio</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead>Procesados</TableHead>
                            <TableHead>Enviados</TableHead>
                            <TableHead>Errores</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {logs.map((log, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Badge variant="outline">
                                  {log.tipo === "scheduled_campaigns" ? (
                                    <><Calendar className="w-3 h-3 mr-1" />Campañas</>
                                  ) : (
                                    <><Bell className="w-3 h-3 mr-1" />Recordatorios</>
                                  )}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">{formatDate(log.inicio)}</TableCell>
                              <TableCell>
                                {log.estado === "completado" ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Completado
                                  </Badge>
                                ) : log.estado === "error" ? (
                                  <Badge variant="destructive">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Error
                                  </Badge>
                                ) : (
                                  <Badge className="bg-blue-100 text-blue-800">
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                    En progreso
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {log.tipo === "scheduled_campaigns" 
                                  ? log.campanas_procesadas || 0
                                  : log.campanas_procesadas || 0}
                              </TableCell>
                              <TableCell className="font-medium text-green-600">
                                {log.tipo === "scheduled_campaigns" 
                                  ? log.emails_enviados || 0
                                  : log.recordatorios_enviados || 0}
                              </TableCell>
                              <TableCell>
                                {log.errores?.length > 0 ? (
                                  <Badge variant="destructive">{log.errores.length}</Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EncuestasSchedulerPage;
